import React, { useState, useRef, useEffect } from 'react'
import { useForm } from '@inertiajs/react'
import type { User } from '../../types'
import { sortPeople } from '../../utils/peopleSort'
import {
  defaultActionItemOwnerId,
  NO_ELIGIBLE_OWNERS_MESSAGE,
  OWNER_PLACEHOLDER,
} from '../../utils/actionItemOwners'

interface InlineAddRowProps {
  teamId: number
  teamMembers: User[]
  currentUserId: number
}

export default function InlineAddRow({ teamId, teamMembers, currentUserId }: InlineAddRowProps) {
  const [isOpen, setIsOpen] = useState(false)
  const titleRef = useRef<HTMLInputElement>(null)
  const defaultOwnerId = defaultActionItemOwnerId(teamMembers, currentUserId)
  const hasEligibleOwners = teamMembers.length > 0
  const isOwnerEligible = (ownerId: string) =>
    teamMembers.some(member => String(member.id) === ownerId)
  const eligibleOwnerIds = teamMembers
    .map(member => String(member.id))
    .sort()
    .join(',')

  const defaultDueDate = new Date()
  defaultDueDate.setDate(defaultDueDate.getDate() + 7)
  const defaultDueDateStr = defaultDueDate.toISOString().split('T')[0]

  const { data, setData, post, processing, reset, errors, transform, clearErrors } = useForm({
    title: '',
    owner_id: defaultOwnerId,
    due_date: defaultDueDateStr,
  })

  useEffect(() => {
    if (isOpen && titleRef.current) titleRef.current.focus()
  }, [isOpen])

  useEffect(() => {
    if (!data.owner_id || isOwnerEligible(data.owner_id)) return

    setData(prev => {
      if (!prev.owner_id || isOwnerEligible(prev.owner_id)) return prev
      return {
        ...prev,
        owner_id: defaultActionItemOwnerId(teamMembers, currentUserId),
      }
    })
    // Deliberately depend on eligible IDs, not the teamMembers array identity:
    // heartbeat prop objects must not reset an in-progress title or owner.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eligibleOwnerIds, currentUserId])

  const titleError = errors.title
  const ownerError = errors.owner_id || (errors as Record<string, string>).owner
  const dueDateError = errors.due_date
  const baseError = (errors as Record<string, string>).base

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!data.title.trim() || !data.owner_id || !isOwnerEligible(data.owner_id)) return

    transform(formData => ({
      action_item: {
        title: formData.title,
        owner_id: formData.owner_id,
        due_date: formData.due_date,
      },
    }))

    post(`/teams/${teamId}/action_items`, {
      preserveScroll: true,
      onSuccess: () => {
        const newDefaultDate = new Date()
        newDefaultDate.setDate(newDefaultDate.getDate() + 7)
        reset()
        setData({
          title: '',
          owner_id: defaultActionItemOwnerId(teamMembers, currentUserId),
          due_date: newDefaultDate.toISOString().split('T')[0],
        })
        clearErrors()
        setTimeout(() => titleRef.current?.focus(), 50)
      }
    })
  }

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-text-muted hover:text-text-secondary hover:bg-[#F8F9FA] transition-colors"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="12" y1="5" x2="12" y2="19" />
          <line x1="5" y1="12" x2="19" y2="12" />
        </svg>
        Add Action Item
      </button>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="grid grid-cols-[32px_1fr_160px_120px_80px_40px] items-center gap-3 px-4 py-2 border-b border-gray-100 bg-charcoal/5">
      {baseError && <p className="col-span-full text-xs text-status-off-track" role="alert">{baseError}</p>}
      <div className="w-5 h-5" />

      <div className="flex flex-col min-w-0 overflow-hidden">
        <input
          ref={titleRef}
          value={data.title}
          onChange={e => setData('title', e.target.value)}
          placeholder="What needs to be done?"
          aria-invalid={!!titleError}
          className={`text-sm border rounded px-2 py-1 outline-none bg-white ${titleError ? 'border-status-off-track focus:border-status-off-track' : 'border-stone focus:border-charcoal'}`}
          onKeyDown={e => {
            if (e.key === 'Escape') setIsOpen(false)
          }}
        />
        {titleError && <p className="text-xs text-status-off-track mt-1">{titleError}</p>}
      </div>

      <div className="flex flex-col min-w-0 overflow-hidden">
        <select
          value={data.owner_id}
          onChange={e => setData('owner_id', e.target.value)}
          disabled={!hasEligibleOwners}
          required
          aria-invalid={!!ownerError}
          className={`text-sm border rounded px-2 py-1 outline-none bg-white ${ownerError ? 'border-status-off-track focus:border-status-off-track' : 'border-stone focus:border-charcoal'}`}
        >
          <option value="">{OWNER_PLACEHOLDER}</option>
          {sortPeople(teamMembers).map(m => (
            <option key={m.id} value={m.id}>{m.full_name}</option>
          ))}
        </select>
        {ownerError && <p className="text-xs text-status-off-track mt-1">{ownerError}</p>}
        {!hasEligibleOwners && !ownerError && (
          <p className="text-xs text-text-muted mt-1">{NO_ELIGIBLE_OWNERS_MESSAGE}</p>
        )}
      </div>

      <div className="flex flex-col min-w-0 overflow-hidden">
        <input
          type="date"
          value={data.due_date}
          onChange={e => setData('due_date', e.target.value)}
          aria-invalid={!!dueDateError}
          className={`text-sm border rounded px-2 py-1 outline-none bg-white ${dueDateError ? 'border-status-off-track focus:border-status-off-track' : 'border-stone focus:border-charcoal'}`}
        />
        {dueDateError && <p className="text-xs text-status-off-track mt-1">{dueDateError}</p>}
      </div>

      <div className="flex items-center gap-1">
        <button
          type="submit"
          disabled={processing || !data.title.trim() || !data.owner_id || !isOwnerEligible(data.owner_id)}
          className="text-sm px-2 py-1 bg-charcoal text-white rounded hover:bg-charcoal-deep disabled:opacity-50"
        >
          Add
        </button>
      </div>

      <button
        type="button"
        onClick={() => setIsOpen(false)}
        className="p-1 rounded hover:bg-gray-200 text-text-muted"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </form>
  )
}
